<template>
  <div>
    <validation-observer ref="form" novalidate>
      <div class="row">
        <div class="col-12">
          <section-title
            container-class="tag-badge-main-info mt-4 mb-1"
            :content="sectionTitle"
            icon="fas fa-clipboard-list-check"
          />
          <div v-if="hasOccupationalPairing" class="mb-4">
            <section-title content="Resumen del Pareo Ocupacional:" has-line />
            <validation-provider
              v-if="!isReadOnly"
              v-slot="{ errors }"
              rules="required"
            >
              <custom-text-area
                v-model="model.summary"
                :count-classes="['d-block', 'small-counter', 'text-right']"
                custom-error-msg="Campo requerido"
                :disabled="isReadOnly"
                :error="errors[0]"
                label="Resumen:"
                :maxlength="5000"
                show-char-count
              />
            </validation-provider>
            <div v-else class="d-flex flex-column">
              <label class="text-muted" style="font-size: 14px">
                Resumen:
              </label>
              <span>{{ model.summary }}</span>
            </div>
          </div>

          <section-title content="Conclusiones Finales:" has-line />
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            rules="required"
          >
            <custom-text-area
              v-model="model.finalConclusions"
              :count-classes="['d-block', 'small-counter', 'text-right']"
              custom-error-msg="Campo requerido"
              :disabled="isReadOnly"
              :error="errors[0]"
              label="Conclusiones Finales:"
              :maxlength="5000"
              show-char-count
            />
          </validation-provider>
          <div v-else class="d-flex flex-column">
            <label class="text-muted" style="font-size: 14px">
              Conclusiones Finales:
            </label>
            <span>{{ model.finalConclusions }}</span>
          </div>
        </div>
      </div>
    </validation-observer>
    <section-title content="Documentos:" has-line />
    <div class="document-row">
      <validation-observer v-if="!isReadOnly" ref="addDocumentForm" slim>
        <div class="row align-items-start">
          <div class="col-md-4">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-input
                v-model="documentName"
                custom-error-msg="Favor de indicar el nombre del documento."
                :error="errors[0]"
                label="Nombre:"
                :maxlength="'100'"
              />
            </validation-provider>
          </div>
          <div class="col-md-6">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-file-input
                ref="documentFileInput"
                v-model="selectedDocument"
                :accept="acceptedDocumentTypes"
                custom-error-msg="Favor de anejar el documento."
                display-style="normal"
                :error="errors[0]"
                label="Documento:"
                label-position="left"
                :on-download="() => {}"
              />
            </validation-provider>
          </div>
          <div class="col-md-2 btn-save-document">
            <action-button
              i="fas fa-file-plus"
              text="Añadir"
              variant="success"
              @click="addDocument"
            />
          </div>
        </div>
      </validation-observer>
    </div>
    <div
      v-for="(document, index) in visibleDocuments"
      :key="document.id"
      class="document-row row"
    >
      <div class="col-md-4">
        <div class="color-gray-6 font-14 font-weight-normal">
          Nombre de Documento:
        </div>
        <div class="color-black font-16 font-weight-normal">
          {{ document.name }}
        </div>
      </div>
      <div class="col-md-5">
        <div class="color-gray-6 font-14 font-weight-normal">
          Archivo:
        </div>
        <div class="color-black font-16 font-weight-normal">
          {{ document.fileName }}
        </div>
      </div>
      <div class="col-md-3 d-flex justify-content-end">
        <action-button
          class="mr-2 mt-1"
          i="fas fa-file-download text-white mr-n1 pr-1"
          variant="success"
          @click="downloadDocument(document)"
        />
        <action-button
          v-if="!isReadOnly"
          class="mt-1"
          i="fas fa-times text-danger mr-n1 pr-1"
          variant="outline-danger"
          @click="removeDocument(index)"
        />
      </div>
    </div>

    <div v-if="canShowMoreDocuments" class="row">
      <div class="col-12 d-flex justify-content-center my-3">
        <action-button
          variant="outline-success custom-view-more actionButton rounded-pill"
          @click="loadMoreDocuments"
        >
          Ver más
          <i class="ml-1 fas fa-chevron-down" />
        </action-button>
      </div>
    </div>
  </div>
</template>

<script>
import { serialize as objectToFormData } from 'object-to-formdata';

//mixins
import alertMixin from '@/mixins/alert.mixin.js';

//utils
import { copy } from '@/utils/objUtils';

//components
import SectionTitle from '@/shared/components/SectionTitle.vue';
import CustomTextArea from 'CustomTextArea';
import ActionButton from 'ActionButton';
import CustomFileInput from 'CustomFileInput';
import CustomInput from 'CustomInput';

//services
import _generalApi from '@/modules/app/api';
import {
  getNine,
  saveDocument,
  removeDocument,
} from '@/modules/vocational-evaluation/api.js';

export default {
  name: 'VocationalEducationPartNine',
  components: {
    SectionTitle,
    CustomTextArea,
    ActionButton,
    CustomFileInput,
    CustomInput,
  },
  mixins: [alertMixin],
  props: {
    id: {
      type: [Number, String],
      default: () => null,
    },
    appointmentId: {
      type: [Number, String],
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
    initialModel: {
      type: Object,
      required: false,
      default: () => null,
    },
    keepUpdates: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    isReadOnly: {
      type: Boolean,
      required: true,
      default: () => false,
    },
    hasOccupationalPairing: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data() {
    return {
      acceptedDocumentTypes: '.pdf,.jpg,.jpeg,.png',
      documentName: null,
      selectedDocument: null,
      visibleDocumentsCount: 5,
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    filled() {
      return this.initialModel?.filled ?? false;
    },
    sectionTitle() {
      return this.hasOccupationalPairing
        ? 'IX. Resumen del Pareo Ocupacional y Conclusiones Finales'
        : 'IX. Conclusiones Finales';
    },
    documents() {
      return this.model.documents || [];
    },
    visibleDocuments() {
      return this.documents.slice(0, this.visibleDocumentsCount);
    },
    canShowMoreDocuments() {
      return this.documents.length > this.visibleDocumentsCount;
    },
  },
  beforeDestroy() {
    this.$emit('update:initialModel', this.model);
  },
  async mounted() {
    this.$emit('update:disabled', true);
    await this.get();
    this.$emit('update:disabled', false);
  },
  methods: {
    setModelFrom(value) {
      const data = copy(value);
      this.model = data;
      this.$emit('update:initialModel', data);
    },
    async get() {
      if (this.keepUpdates && this.filled) {
        this.setModelFrom(this.initialModel);
        return;
      }

      if (!this.appointmentId) return;
      const { data } = await getNine({
        id: this.id,
        appointmentId: this.appointmentId,
      });
      this.setModelFrom({ ...data, filled: true });
    },
    async validate() {
      return await this.$refs.form.validate();
    },
    async addDocument() {
      if (!this.selectedDocument) return;

      await this.$nextTick();
      const valid = await this.$refs.addDocumentForm.validate();
      if (!valid) return;

      const payload = objectToFormData(
        { name: this.documentName, file: this.selectedDocument },
        { indices: true, nullsAsUndefineds: true }
      );

      let data;
      try {
        ({ data } = await saveDocument(this.id, payload));
      } catch {
        this.ShowToast(
          '',
          'Ocurrió un error al intentar guardar el documento.',
          'error'
        );
        return;
      }

      this.model = {
        ...this.model,
        documents: [
          ...this.documents,
          {
            id: data?.id ?? data,
            documentId: data?.documentId ?? data,
            name: this.documentName,
            fileName: this.selectedDocument.name,
          },
        ],
      };
      this.ShowToast('', 'Documento guardado exitosamente.', 'success');
      this.documentName = null;
      this.$refs.documentFileInput.removeFile();
      this.$refs.addDocumentForm.reset();
    },
    async downloadDocument(document) {
      await _generalApi.downloadDocument(document.documentId);
    },
    async removeDocument(index) {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea eliminar el documento?',
        'Sí',
        'No'
      );
      if (!confirmed) return;

      try {
        await removeDocument(this.id, this.documents[index].id);
      } catch {
        this.ShowToast(
          '',
          'Ocurrió un error al intentar eliminar el documento.',
          'error'
        );
        return;
      }

      const documents = [...this.documents];
      documents.splice(index, 1);
      this.model = { ...this.model, documents };
    },
    loadMoreDocuments() {
      this.visibleDocumentsCount += 5;
    },
  },
};
</script>

<style scoped>
.document-row {
  align-items: center;
  background-color: #f8f8f8;
  border-radius: 4px;
  margin: 1rem 0 0;
  min-height: 78px;
  padding: 1rem;
}
.btn-save-document {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 32px;
}
</style>
